import React, { useState, useCallback, useEffect } from 'react';
import {
  Modal,
  Text,
  BlockStack,
  TextField,
  Autocomplete,
  DropZone,
  Icon,
  Button,
} from '@shopify/polaris';
import {
  SearchMinor,
  UploadMajor,
  MagicMajor,
} from '@shopify/polaris-icons';
import { generationsService } from '../../services/generationsService';
import { authService } from '../../services/authService';

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const CreateGenerationModal = ({ 
  isOpen, 
  onClose, 
  setToastMessage, 
  setToastActive,
  onGenerationCreated 
}) => {
  const [isCreatingGeneration, setIsCreatingGeneration] = useState(false);
  const [generationFiles, setGenerationFiles] = useState([]);
  const [newGenerationData, setNewGenerationData] = useState({
    kid_name: '',
    slug: '',
    kid_image: '',
    generation_id: '',
    order_id: ''
  });
  const [selectedStoryForGeneration, setSelectedStoryForGeneration] = useState([]);
  const [generationStories, setGenerationStories] = useState([]);
  const [isLoadingStories, setIsLoadingStories] = useState(false);
  const [generationStoryAutocompleteInputValue, setGenerationStoryAutocompleteInputValue] = useState('');
  const [generationStoryAutocompleteOptions, setGenerationStoryAutocompleteOptions] = useState([]);

  // Upload file via backend (avoids CORS issues)
  const uploadFileToBackend = useCallback(async (file, kidName, storyName) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = async () => {
        try {
          const base64Image = reader.result; // data:image/jpeg;base64,...

          // Sanitize and build filename
          const sanitize = str => (str || '').replace(/[^a-zA-Z0-9]/g, '_');
          const ext = file.name.split('.').pop();
          const randomTxt = `${Math.random().toString(36).substring(2, 6)}${Date.now()}`;
          const filename = `${sanitize(randomTxt)}_${sanitize(kidName)}_${sanitize(storyName)}.${ext}`;

          console.log('📤 Uploading image to backend...');

          const response = await fetch(`${SERVER_URL}/upload-kid-image`, {
            method: 'POST',
            headers: {
              ...authService.getAuthHeaders(),
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              image: base64Image,
              filename: filename
            })
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Upload failed: ${response.status}`);
          }

          const data = await response.json();

          if (data.success) {
            console.log('✅ Image uploaded successfully:', data.url);
            resolve(data.url);
          } else {
            throw new Error(data.error || 'Upload failed');
          }
        } catch (error) {
          console.error('❌ Upload error:', error);
          reject(error);
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      reader.readAsDataURL(file);
    });
  }, []);

  // Fetch generation stories
  const fetchGenerationStories = useCallback(async () => {
    try {
      const data = await generationsService.getAllGenerationStories();
      setGenerationStories(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      setGenerationStories([]);
    }
  }, []);

  // Handler for story autocomplete change for generation
  const handleGenerationStoryAutocompleteChange = useCallback((value) => {
    setGenerationStoryAutocompleteInputValue(value);
    if (value === '') {
      const allOptions = generationStories.map(story => ({ label: story.title, value: story.slug }));
      setGenerationStoryAutocompleteOptions(allOptions);
      return;
    }
    const filterRegex = new RegExp(value, 'i');
    const filteredOptions = generationStories
      .filter(story => story.title.match(filterRegex) || story.slug.match(filterRegex))
      .map(story => ({ label: story.title, value: story.slug }));
    setGenerationStoryAutocompleteOptions(filteredOptions);
  }, [generationStories]);

  // Handler for story autocomplete select for generation
  const handleGenerationStoryAutocompleteSelect = useCallback((selected) => {
    setSelectedStoryForGeneration(selected);
    const selectedSlug = selected[0] || '';
    setNewGenerationData(prev => ({ ...prev, slug: selectedSlug }));
    const selectedStory = generationStories.find(story => story.slug === selectedSlug);
    setGenerationStoryAutocompleteInputValue(selectedStory ? selectedStory.title : '');
  }, [generationStories]);

  // Load data when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchGenerationStories();
    }
  }, [isOpen, fetchGenerationStories]);

  // Initialize autocomplete options when generationStories changes
  useEffect(() => {
    setGenerationStoryAutocompleteOptions(generationStories.map(story => ({ label: story.title, value: story.slug })));
  }, [generationStories]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setNewGenerationData({ kid_name: '', slug: '', kid_image: '', generation_id: '', order_id: '' });
      setGenerationFiles([]);
      setSelectedStoryForGeneration([]);
      setGenerationStoryAutocompleteInputValue('');
    }
  }, [isOpen]);

  const handleCreateGeneration = async () => {
    if (!newGenerationData.kid_name || !newGenerationData.slug || generationFiles.length === 0) {
      setToastMessage('Please fill in all required fields and upload a kid image');
      setToastActive(true);
      setTimeout(() => {
        setToastActive(false);
      }, 3000);
      return;
    }
    if (newGenerationData.kid_name.length > 20) {
      setToastMessage('Kid name must be 20 characters or less');
      setToastActive(true);
      setTimeout(() => {
        setToastActive(false);
      }, 3000);
      return;
    }
    setIsCreatingGeneration(true);
    try {
      // Upload file via backend (no CORS issues)
      console.log('📤 Starting image upload...');
      const uploadedImageUrl = await uploadFileToBackend(
        generationFiles[0],
        newGenerationData.kid_name,
        generationStoryAutocompleteInputValue
      );
      console.log('✅ Image uploaded:', uploadedImageUrl);

      // Generate random number for generation_id
      const timestamp = Date.now();
      const randomSuffix = Math.random().toString(36).substring(2, 8);
      const generationId = `GEN${timestamp}-${randomSuffix}`;

      const requestData = {
        slug: newGenerationData.slug,
        kid_name: newGenerationData.kid_name,
        kid_image: uploadedImageUrl,
        generation_id: generationId,
        order_id: newGenerationData.order_id
      };

      const selectedStory = generationStories.find((story) => story.slug === newGenerationData.slug);
      if (selectedStory) {
        requestData.generation_provider_override = selectedStory.generation_provider_override || '';
        requestData.generation_model_override = selectedStory.generation_model_override || '';
      }

      console.log('🎨 Creating generation with data:', requestData);
      await generationsService.createGeneration(requestData);

      setToastMessage('Generation Created Successfully');
      setToastActive(true);
      setTimeout(() => {
        setToastActive(false);
      }, 3000);

      // Reset form
      setNewGenerationData({ kid_name: '', slug: '', kid_image: '', generation_id: '', order_id: '' });
      setGenerationFiles([]);
      setSelectedStoryForGeneration([]);
      setGenerationStoryAutocompleteInputValue('');

      onClose();

      // Notify parent component to refresh generations list
      if (onGenerationCreated) {
        onGenerationCreated();
      }
    } catch (error) {
      console.error('❌ Error creating generation:', error);
      const errorMessage = error.message || 'Failed to create generation';
      setToastMessage(`Error: ${errorMessage}`);
      setToastActive(true);
      setTimeout(() => {
        setToastActive(false);
      }, 5000);
    } finally {
      setIsCreatingGeneration(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={<div style={{padding: '16px'}}>{'Create New Generation'}</div>}
      primaryAction={{
        content: (
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon source={MagicMajor} tone="inverse" color="white" />
            Generate
          </span>
        ),
        onAction: handleCreateGeneration,
        loading: isCreatingGeneration,
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: onClose,
        },
      ]}
      footer={<div style={{padding: '16px'}}></div>}
    >
      <BlockStack gap="400">
        {/* Kid Image Upload */}
        <div>
          <Text variant="bodyMd" as="label" style={{ display: 'block', marginBottom: 8 }}>Upload Kid Image</Text>
          <DropZone
            onDrop={(_dropFiles, acceptedFiles) => {
              if (acceptedFiles.length > 0) {
                setGenerationFiles([acceptedFiles[0]]);
              }
            }}
            accept="image/jpeg, image/png"
            variableHeight
            style={{
              margin: '16px 0',
              padding: '32px 0',
              display: 'block',
              width: '100%',
              textAlign: 'center'
            }}
          >
            {generationFiles.length > 0 ? (
              <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, width: '100%' }}>
                <button
                  onClick={() => setGenerationFiles([])}
                  style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    background: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: 28,
                    height: 28,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: 20,
                    fontWeight: 'bold',
                    zIndex: 2
                  }}
                  aria-label="Remove image"
                >
                  ×
                </button>
                <img
                  src={window.URL.createObjectURL(generationFiles[0])}
                  alt={generationFiles[0].name}
                  style={{
                    maxWidth: '250px',
                    maxHeight: '250px',
                    width: '100%',
                    height: 'auto',
                    borderRadius: 12,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    objectFit: 'contain',
                    marginTop: 4
                  }}
                />
                <Text variant="bodyMd" as="p" style={{ margin: 0 }}>{generationFiles[0].name}</Text>
                <Text variant="bodySm" tone="subdued" style={{ marginBottom: 20 }}>
                  {(generationFiles[0].size / (1024 * 1024)).toFixed(2)} MB
                </Text>
              </div>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                width: '100%',
                padding: '16px 0'
              }}>
                <Icon source={UploadMajor} tone="base" />
                <Text variant="bodyMd" as="p" style={{ margin: 0 }}>Add Image</Text>
                <Text variant="bodySm" tone="subdued" style={{ margin: 0 }}>Accepts .jpg, and .png</Text>
              </div>
            )}
          </DropZone>
        </div>
        {/* Kid Name */}
        <TextField
          label="Kid Name"
          value={newGenerationData.kid_name}
          onChange={(value) => setNewGenerationData(prev => ({ ...prev, kid_name: value }))}
          placeholder="Enter kid name (max 20 characters)"
          maxLength={20}
          required
        />
        {/* Order ID */}
        <TextField
          label="Order ID"
          value={newGenerationData.order_id}
          onChange={(value) => setNewGenerationData(prev => ({ ...prev, order_id: value }))}
          placeholder="Enter order ID"
        />
        {/* Story Selection */}
        <Autocomplete
          options={generationStoryAutocompleteOptions}
          selected={selectedStoryForGeneration}
          onSelect={handleGenerationStoryAutocompleteSelect}
          loading={isLoadingStories}
          textField={
            <Autocomplete.TextField
              onChange={handleGenerationStoryAutocompleteChange}
              label="Story"
              value={generationStoryAutocompleteInputValue}
              prefix={<Icon source={SearchMinor} tone="base" />}
              placeholder="Search for a story..."
              autoComplete="off"
              required
            />
          }
        />
      </BlockStack>
    </Modal>
  );
};

export default CreateGenerationModal;
